import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  Phone,
  MessageSquare,
  CheckCircle2,
  Calendar as CalendarIcon,
} from "lucide-react";
import { format, parse } from "date-fns";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import {
  API,
  SMS_HREF,
  TEL_HREF,
  PHONE_DISPLAY,
  INITIAL_CONTACT_FORM,
  TIME_WINDOW_OPTIONS,
  reveal,
} from "./constants";
import { track } from "@/lib/analytics";

export function ContactForm() {
  const [form, setForm] = useState(INITIAL_CONTACT_FORM);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [bookedWindows, setBookedWindows] = useState([]);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  // When the user picks a date, fetch already-booked time windows
  // so we can grey them out and avoid double-booking.
  React.useEffect(() => {
    if (!form.preferred_date) {
      setBookedWindows([]);
      return;
    }
    let cancelled = false;
    axios
      .get(`${API}/availability`, { params: { date: form.preferred_date } })
      .then((res) => {
        if (cancelled) return;
        const booked = res?.data?.booked || [];
        setBookedWindows(booked);
        // If currently selected window is now taken, clear it
        if (
          form.preferred_time_window &&
          form.preferred_time_window !== "flexible" &&
          booked.includes(form.preferred_time_window)
        ) {
          update("preferred_time_window", "");
        }
      })
      .catch(() => {
        if (!cancelled) setBookedWindows([]);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.preferred_date]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.address || !form.service_type) {
      toast.error("Please fill in name, phone, address, and service type.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      if (!payload.preferred_date) delete payload.preferred_date;
      if (!payload.preferred_time_window) delete payload.preferred_time_window;
      await axios.post(`${API}/contact`, payload);
      track("form_submit", { service_type: form.service_type || "unknown" });
      toast.success("Got it! Mike will reach out shortly.");
      setSubmitted(true);
      setForm(INITIAL_CONTACT_FORM);
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Something went wrong. Please text me instead."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative pt-8 pb-4 sm:pt-12 sm:pb-6 bg-[#FFFFFF]"
      data-testid="contact-section"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          <motion.div {...reveal}>
            <div className="text-xs font-bold uppercase tracking-[0.22em] text-[#5FBE1F] mb-4">
              Get on the schedule
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-normal text-[#000000] leading-[1.12]">
              Two ways to reach Mike.
            </h2>
            <p className="mt-5 text-[#4A4A46] text-lg leading-relaxed max-w-md">
              Texting is fastest — but if you'd rather send details in writing,
              the form's all yours.
            </p>

            <div className="mt-10 space-y-4">
              <a
                href={SMS_HREF}
                onClick={() => track("text_gutters_click", { location: "contact_section_sms_card" })}
                className="flex items-center gap-4 p-5 bg-[#0A0A0A] text-white rounded-sm hover:bg-[#000000] transition-colors"
                data-testid="contact-sms-link"
              >
                <div className="w-12 h-12 bg-[#5FBE1F] rounded-sm flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.22em] text-[#C9C9C9]">
                    Fastest · Text "Gutters"
                  </div>
                  <div className="font-display text-xl">{PHONE_DISPLAY}</div>
                </div>
              </a>

              <a
                href={TEL_HREF}
                onClick={() => track("phone_call_click", { location: "contact_section_phone_card" })}
                className="flex items-center gap-4 p-5 bg-white border border-[#E5E5E5] rounded-sm hover:border-[#5FBE1F] transition-colors"
                data-testid="contact-phone-link"
              >
                <div className="w-12 h-12 bg-[#F5F5F5] rounded-sm flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-[#0A0A0A]" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.22em] text-[#4A4A46]">
                    Prefer to call
                  </div>
                  <div className="font-display text-xl text-[#000000]">
                    {PHONE_DISPLAY}
                  </div>
                </div>
              </a>
            </div>
          </motion.div>

          <motion.form
            {...reveal}
            onSubmit={onSubmit}
            className="bg-white border border-[#E5E5E5] p-8 sm:p-10 rounded-sm"
            data-testid="contact-form"
          >
            {submitted ? (
              <div className="py-10 text-center" data-testid="contact-form-success">
                <div className="w-14 h-14 mx-auto bg-[#0A0A0A] rounded-sm flex items-center justify-center">
                  <CheckCircle2 className="w-7 h-7 text-white" />
                </div>
                <h3 className="mt-5 font-display text-2xl text-[#000000]">
                  Thanks — request received.
                </h3>
                <p className="mt-3 text-[#4A4A46]">
                  Mike will text or call you back shortly to schedule.
                </p>
                <button
                  type="button"
                  onClick={() => setSubmitted(false)}
                  className="mt-6 text-sm font-semibold text-[#5FBE1F] hover:underline"
                  data-testid="contact-form-reset"
                >
                  Send another request
                </button>
              </div>
            ) : (
              <>
                <h3 className="font-display text-2xl text-[#000000]">
                  Request service
                </h3>
                <p className="text-sm text-[#4A4A46] mt-1">
                  Fields marked * are required.
                </p>

                <div className="mt-6 space-y-5">
                  <div>
                    <Label htmlFor="name" className="text-sm font-semibold text-[#000000] mb-2 block">
                      Your name *
                    </Label>
                    <Input
                      id="name"
                      data-testid="contact-input-name"
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      className="bg-white border-[#E5E5E5] focus-visible:ring-[#5FBE1F] rounded-sm h-12"
                      placeholder="Jane Doe"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="phone" className="text-sm font-semibold text-[#000000] mb-2 block">
                        Phone *
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        data-testid="contact-input-phone"
                        value={form.phone}
                        onChange={(e) => update("phone", e.target.value)}
                        className="bg-white border-[#E5E5E5] focus-visible:ring-[#5FBE1F] rounded-sm h-12"
                        placeholder="(712) 555-0100"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-sm font-semibold text-[#000000] mb-2 block">
                        Email
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        data-testid="contact-input-email"
                        value={form.email}
                        onChange={(e) => update("email", e.target.value)}
                        className="bg-white border-[#E5E5E5] focus-visible:ring-[#5FBE1F] rounded-sm h-12"
                        placeholder="you@email.com"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="address" className="text-sm font-semibold text-[#000000] mb-2 block">
                      Service address *
                    </Label>
                    <Input
                      id="address"
                      data-testid="contact-input-address"
                      value={form.address}
                      onChange={(e) => update("address", e.target.value)}
                      className="bg-white border-[#E5E5E5] focus-visible:ring-[#5FBE1F] rounded-sm h-12"
                      placeholder="123 Main St, Glenwood, IA"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="service" className="text-sm font-semibold text-[#000000] mb-2 block">
                      Service type *
                    </Label>
                    <Select
                      value={form.service_type}
                      onValueChange={(v) => update("service_type", v)}
                    >
                      <SelectTrigger
                        id="service"
                        className="bg-white border-[#E5E5E5] focus:ring-[#5FBE1F] rounded-sm h-12"
                        data-testid="contact-input-service-type"
                      >
                        <SelectValue placeholder="Choose a service" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Gutter Cleaning ($100 flat)" data-testid="option-cleaning">
                          Gutter Cleaning · $100 flat
                        </SelectItem>
                        <SelectItem value="Gutter Cover Installation" data-testid="option-covers">
                          Gutter Cover Installation
                        </SelectItem>
                        <SelectItem value="Gutter Repairs (custom quote, $100 min)" data-testid="option-repairs">
                          Gutter Repairs — Custom Quote ($100 min)
                        </SelectItem>
                        <SelectItem value="Gutter Reseal Service ($195)" data-testid="option-reseal">
                          Gutter Reseal Service — $195
                        </SelectItem>
                        <SelectItem value="Window Screen Replacement" data-testid="option-screens">
                          Window Screen Replacement
                        </SelectItem>
                        <SelectItem value="Not sure — please advise" data-testid="option-other">
                          Not sure — please advise
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-semibold text-[#000000] mb-2 block">
                        Preferred date
                      </Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            data-testid="contact-input-preferred-date"
                            className="w-full h-12 px-3 flex items-center justify-between bg-white border border-[#E5E5E5] rounded-sm text-left text-sm text-[#000000] hover:border-[#5FBE1F] focus:outline-none focus:ring-2 focus:ring-[#5FBE1F]"
                          >
                            <span className={form.preferred_date ? "" : "text-[#9A9A95]"}>
                              {form.preferred_date
                                ? format(
                                    parse(form.preferred_date, "yyyy-MM-dd", new Date()),
                                    "EEE, MMM d, yyyy"
                                  )
                                : "Pick a day"}
                            </span>
                            <CalendarIcon className="w-4 h-4 text-[#4A4A46]" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={
                              form.preferred_date
                                ? parse(form.preferred_date, "yyyy-MM-dd", new Date())
                                : undefined
                            }
                            onSelect={(d) =>
                              update(
                                "preferred_date",
                                d ? format(d, "yyyy-MM-dd") : ""
                              )
                            }
                            disabled={(d) => {
                              const today = new Date();
                              today.setHours(0, 0, 0, 0);
                              return d < today;
                            }}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div>
                      <Label htmlFor="time-window" className="text-sm font-semibold text-[#000000] mb-2 block">
                        Preferred time
                      </Label>
                      <Select
                        value={form.preferred_time_window}
                        onValueChange={(v) => update("preferred_time_window", v)}
                      >
                        <SelectTrigger
                          id="time-window"
                          className="bg-white border-[#E5E5E5] focus:ring-[#5FBE1F] rounded-sm h-12"
                          data-testid="contact-input-time-window"
                        >
                          <SelectValue placeholder="Pick a window" />
                        </SelectTrigger>
                        <SelectContent>
                          {TIME_WINDOW_OPTIONS.map((opt) => {
                            const isBooked = bookedWindows.includes(opt.value);
                            return (
                              <SelectItem
                                key={opt.value}
                                value={opt.value}
                                disabled={isBooked}
                                data-testid={`option-time-${opt.value}`}
                              >
                                {opt.label}
                                {isBooked && (
                                  <span className="ml-2 text-xs text-[#A84B34]">
                                    · already booked
                                  </span>
                                )}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      {form.preferred_date && bookedWindows.length > 0 && (
                        <p
                          className="mt-2 text-xs text-[#4A4A46]"
                          data-testid="availability-note"
                        >
                          {bookedWindows.length === 1
                            ? "1 window is already booked for this date."
                            : `${bookedWindows.length} windows are already booked for this date.`}
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-sm font-semibold text-[#000000] mb-2 block">
                      Anything I should know?
                    </Label>
                    <Textarea
                      id="message"
                      data-testid="contact-input-message"
                      value={form.message}
                      onChange={(e) => update("message", e.target.value)}
                      className="bg-white border-[#E5E5E5] focus-visible:ring-[#5FBE1F] rounded-sm min-h-[110px]"
                      placeholder="Two-story home, side gate code, etc."
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#5FBE1F] hover:bg-[#4A9818] text-white font-bold h-12 rounded-sm transition-all"
                    data-testid="contact-form-submit"
                  >
                    {loading ? "Sending…" : "Send request"}
                  </Button>

                  <p className="text-xs text-[#4A4A46] text-center">
                    Prefer speed?{" "}
                    <a
                      href={SMS_HREF}
                      onClick={() => track("text_gutters_click", { location: "contact_form_inline_link" })}
                      className="text-[#5FBE1F] font-semibold underline"
                    >
                      Text "Gutters" to {PHONE_DISPLAY}
                    </a>
                  </p>
                </div>
              </>
            )}
          </motion.form>
        </div>
      </div>
    </section>
  );
}
